<template>
  <VForm ref="form" lazy-validation class="ns-credit-card-form">
    <h2>
      <span class="title-small-bold">{{ t.sepaFormTitle }}</span>
      <span class="secure">
        <NsIcon icon-name="icon-lock" /> <span>{{ t.secure }}</span>
      </span>
    </h2>
    <img
      class="p-icon-large"
      src="https://www.nuskin.com/content/dam/apps/checkout/creditcards/v2/CC-54.png"
    />
    <VTextField
      v-model="sepaNumber"
      required
      :label="t.ibanLabel"
      class="flex-column"
      validate-on-blur
      autofocus
      :rules="rules"
      @input="sepaChanged"
    >
    </VTextField>
  </VForm>
</template>

<script>
import { VForm, VTextField } from 'vuetify/lib';
import { NsIcon } from '@nuskin/ns-icon';
export default {
  name: 'NsSepaForm',
  components: {
    VForm,
    VTextField,
    NsIcon,
  },
  props: {
    value: {
      type: String,
      default() {
        return '';
      },
    },

    /**
     * Translations as a key:value object
     */
    translations: {
      type: Object,
      default() {
        return {
          ibanLabel: 'IBAN',
          fieldRequired: 'Field is required',
          invalidIban: 'Invalid IBAN value',
          secure: 'SECURE',
          sepaFormTitle: 'SEPA Direct Debit',
        };
      },
    },
  },
  data() {
    return {
      rules: [],
    };
  },
  computed: {
    t() {
      return this.translations;
    },
  },
  watch: {
    t() {
      if (Object.keys(this.t).length > 0) {
        // This is a hack.  The only way I have found to reset the rules with new translations
        // is to clear them out and wait for a while then reset them
        this.rules = [];
        setTimeout(() => this.initRules(), 10);
      }
    },
    value(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.sepaNumber = newVal;
      }
    },
  },
  mounted() {
    this.initRules();
  },
  methods: {
    isIbanValid(value) {
      // remove spaces and to upper case
      let iban = value.replace(/ /g, '').toUpperCase(),
        ibancheckdigits = '',
        leadingZeroes = true,
        cRest = '',
        cOperator = '',
        countrycode,
        ibancheck,
        charAt,
        cChar,
        bbanpattern,
        bbancountrypatterns,
        ibanregexp,
        i,
        p;

      if (
        !/^([a-zA-Z0-9]{4} ){2,8}[a-zA-Z0-9]{1,4}|[a-zA-Z0-9]{12,34}$/.test(
          iban
        )
      ) {
        return false;
      }

      // check the country code and find the country specific format
      countrycode = iban.substring(0, 2);
      bbancountrypatterns = {
        AL: '\\d{8}[\\dA-Z]{16}',
        AD: '\\d{8}[\\dA-Z]{12}',
        AT: '\\d{16}',
        AZ: '[\\dA-Z]{4}\\d{20}',
        BE: '\\d{12}',
        BH: '[A-Z]{4}[\\dA-Z]{14}',
        BA: '\\d{16}',
        BR: '\\d{23}[A-Z][\\dA-Z]',
        BG: '[A-Z]{4}\\d{6}[\\dA-Z]{8}',
        CR: '\\d{17}',
        HR: '\\d{17}',
        CY: '\\d{8}[\\dA-Z]{16}',
        CZ: '\\d{20}',
        DK: '\\d{14}',
        DO: '[A-Z]{4}\\d{20}',
        EE: '\\d{16}',
        FO: '\\d{14}',
        FI: '\\d{14}',
        FR: '\\d{10}[\\dA-Z]{11}\\d{2}',
        GE: '[\\dA-Z]{2}\\d{16}',
        DE: '\\d{18}',
        GI: '[A-Z]{4}[\\dA-Z]{15}',
        GR: '\\d{7}[\\dA-Z]{16}',
        GL: '\\d{14}',
        GT: '[\\dA-Z]{4}[\\dA-Z]{20}',
        HU: '\\d{24}',
        IS: '\\d{22}',
        IE: '[\\dA-Z]{4}\\d{14}',
        IL: '\\d{19}',
        IT: '[A-Z]\\d{10}[\\dA-Z]{12}',
        KZ: '\\d{3}[\\dA-Z]{13}',
        KW: '[A-Z]{4}[\\dA-Z]{22}',
        LV: '[A-Z]{4}[\\dA-Z]{13}',
        LB: '\\d{4}[\\dA-Z]{20}',
        LI: '\\d{5}[\\dA-Z]{12}',
        LT: '\\d{16}',
        LU: '\\d{3}[\\dA-Z]{13}',
        MK: '\\d{3}[\\dA-Z]{10}\\d{2}',
        MT: '[A-Z]{4}\\d{5}[\\dA-Z]{18}',
        MR: '\\d{23}',
        MU: '[A-Z]{4}\\d{19}[A-Z]{3}',
        MC: '\\d{10}[\\dA-Z]{11}\\d{2}',
        MD: '[\\dA-Z]{2}\\d{18}',
        ME: '\\d{18}',
        NL: '[A-Z]{4}\\d{10}',
        NO: '\\d{11}',
        PK: '[\\dA-Z]{4}\\d{16}',
        PS: '[\\dA-Z]{4}\\d{21}',
        PL: '\\d{24}',
        PT: '\\d{21}',
        RO: '[A-Z]{4}[\\dA-Z]{16}',
        SM: '[A-Z]\\d{10}[\\dA-Z]{12}',
        SA: '\\d{2}[\\dA-Z]{18}',
        RS: '\\d{18}',
        SK: '\\d{20}',
        SI: '\\d{15}',
        ES: '\\d{20}',
        SE: '\\d{20}',
        CH: '\\d{5}[\\dA-Z]{12}',
        TN: '\\d{20}',
        TR: '\\d{5}[\\dA-Z]{17}',
        AE: '\\d{3}\\d{16}',
        GB: '[A-Z]{4}\\d{14}',
        VG: '[\\dA-Z]{4}\\d{16}',
      };

      bbanpattern = bbancountrypatterns[countrycode];

      // As new countries will start using IBAN in the
      // future, we only check if the countrycode is known.
      // This prevents false negatives, while almost all
      // false positives introduced by this, will be caught
      // by the checksum validation below anyway.
      // Strict checking should return FALSE for unknown
      // countries.
      if (typeof bbanpattern !== 'undefined') {
        ibanregexp = new RegExp('^[A-Z]{2}\\d{2}' + bbanpattern + '$', '');
        if (!ibanregexp.test(iban)) {
          return false; // invalid country specific format
        }
      }

      // now check the checksum, first convert to digits
      ibancheck = iban.substring(4, iban.length) + iban.substring(0, 4);
      for (i = 0; i < ibancheck.length; i++) {
        charAt = ibancheck.charAt(i);
        if (charAt !== '0') {
          leadingZeroes = false;
        }
        if (!leadingZeroes) {
          ibancheckdigits += '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'.indexOf(
            charAt
          );
        }
      }

      // calculate the result of: ibancheckdigits % 97
      for (p = 0; p < ibancheckdigits.length; p++) {
        cChar = ibancheckdigits.charAt(p);
        cOperator = '' + cRest + '' + cChar;
        cRest = cOperator % 97;
      }
      return cRest === 1;
    },
    sepaChanged() {
      this.$emit('input', this.sepaNumber);
    },
    initRules() {
      // No numbers or blank spaces
      this.rules = [
        (v) => {
          return (
            (!!v && !!v.trim()) || this.t.fieldRequired || 'Field is required'
          );
        },
        (v) => {
          return this.isIbanValid(v) || this.t.invalidIban || 'Invalid Iban';
        },
      ];
    },
    validate() {
      return this.$refs.form.validate();
    },
    reset() {
      this.$refs.form.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/colors";

::v-deep *:not(.v-icon) {
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}

.ns-credit-card-form {
  h2 {
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;
  }
  .p-icon-large {
    height: 40px;
  }
  .secure {
    display: flex;
    font-size: 14px;
    text-transform: uppercase;
    line-height: 24px;
    font-weight: map.get(typographyVariables.$typography, font-weight-semibold);

    .icon {
      height: 20px;
      margin-top: -1px;
    }

    span {
      padding-left: 5px;
    }
  }
}
</style>
